import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { entrance, IS_PRERENDER } from "@/components/site/motionPresets";

// `to` marks a real route; everything else is an in-page anchor on the landing
// page. Services is a route because it is its own document, not a section.
const LINKS = [
  { id: "ecosystem", label: "Products" },
  { id: "services", label: "Services", to: "/services" },
  { id: "ethos", label: "About" },
  { id: "connect", label: "Connect" },
];

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("top");
  const mobileToggleRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      requestAnimationFrame(() => mobileToggleRef.current?.focus());
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection(null);
      return undefined;
    }

    const sections = ["top", "ecosystem", "ethos", "connect"]
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const current = entries.find((entry) => entry.isIntersecting);
        if (current) setActiveSection(current.target.id);
      },
      { rootMargin: "-24% 0px -66% 0px", threshold: 0 },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [location.pathname]);

  const handleAnchor = (e, id) => {
    e.preventDefault();
    setOpen(false);
    setActiveSection(id);

    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 80,
        behavior: "smooth",
      });
      return;
    }

    // The section isn't on this route (a policy page). Go home and let the
    // landing page scroll once it has rendered.
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  return (
    <motion.header
      data-testid="site-navbar"
      {...entrance({ y: -24, duration: 0.6 })}
      // Full-width solid bar. The only scroll cue is the hairline strengthening —
      // no shadow, no blur, nothing that implies elevation.
      className={`nav-bar fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "border-line-strong" : "border-line"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-6 lg:px-10 flex items-center gap-2 sm:gap-6 h-16">
        <a
          href={location.pathname === "/" ? "#top" : "/"}
          onClick={(e) => handleAnchor(e, "top")}
          data-testid="navbar-brand"
          className="group flex min-h-11 items-center gap-2.5"
        >
          {/* Decorative: the wordmark beside it already spells the name, so a
              non-empty alt would make a screen reader announce it twice. The
              link takes its accessible name from that text. */}
          {/* The reversed mark (cream + gold): the full-colour green vanishes on
              this background at 1.71:1. It is ~2.3:1, not square, so it is
              sized by height; shrink-0 stops the flex row squeezing it. */}
          <img
            src="/ovd-mark-reversed.svg"
            alt=""
            className="h-7 w-auto shrink-0 sm:h-8"
          />
          {/* OneVidaria in one colour, Digital in brand gold #D9A032 (8.29:1 on
              the navbar) — the same split as the lockup on the OG cover. */}
          <span className="font-display text-sm sm:text-base font-medium tracking-tight text-white">
            OneVidaria <span className="text-[#D9A032]">Digital</span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-1 ml-auto">
          {LINKS.map((l) => {
            const isCurrent = l.to
              ? location.pathname === l.to
              : location.pathname === "/" && activeSection === l.id;
            const className =
              l.id === "connect"
                ? `relative ml-1 inline-flex min-h-11 items-center rounded-sm bg-[#10B981] px-4 py-2 text-xs font-medium text-[#06231A] transition-colors hover:bg-[#34D399] ${
                    isCurrent
                      ? "ring-2 ring-[#34D399]/40 ring-offset-2 ring-offset-surface-0"
                      : ""
                  }`
                : `relative inline-flex min-h-11 items-center rounded-sm px-4 py-2 text-sm transition-colors hover:bg-surface-2 hover:text-white ${
                    isCurrent ? "bg-surface-2 text-white" : "text-white/70"
                  }`;

            return l.to ? (
              <Link
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={className}
                aria-current={isCurrent ? "page" : undefined}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={(e) => handleAnchor(e, l.id)}
                data-testid={`nav-link-${l.id}`}
                className={className}
                aria-current={isCurrent ? "location" : undefined}
              >
                {l.label}
              </a>
            );
          })}
        </div>

        <button
          ref={mobileToggleRef}
          type="button"
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden ml-auto h-11 w-11 grid place-items-center rounded-sm bg-surface-1 border border-line text-white transition-colors hover:border-line-strong"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="navbar-mobile-menu"
        >
          {open ? <X size={16} /> : <Menu size={16} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            id="navbar-mobile-menu"
            initial={IS_PRERENDER ? false : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="md:hidden surface absolute top-full left-4 right-4 mt-2 p-3 flex flex-col"
            data-testid="navbar-mobile-menu"
          >
            {LINKS.map((l) => {
              const isCurrent = l.to
                ? location.pathname === l.to
                : location.pathname === "/" && activeSection === l.id;
              const className =
                l.id === "connect"
                  ? `mt-2 inline-flex min-h-11 items-center justify-center rounded-sm bg-[#10B981] px-4 py-2.5 text-center text-sm font-medium text-[#06231A] transition-colors hover:bg-[#34D399] ${
                      isCurrent ? "ring-2 ring-[#34D399]/40" : ""
                    }`
                  : `inline-flex min-h-11 items-center rounded-sm px-4 py-3 text-sm transition-colors hover:bg-surface-2 hover:text-white ${
                      isCurrent ? "bg-surface-2 text-white" : "text-white/80"
                    }`;

              return l.to ? (
                <Link
                  key={l.id}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className={className}
                  aria-current={isCurrent ? "page" : undefined}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={(e) => handleAnchor(e, l.id)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className={className}
                  aria-current={isCurrent ? "location" : undefined}
                >
                  {l.label}
                </a>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
